// FNV-1a 32-bit hash, used for state.hash. Cheap, well-distributed, deterministic.
// Float values are first interpreted as their IEEE-754 bit pattern so that any
// difference at all between two floats produces a different hash.

const FNV_OFFSET_BASIS = 2166136261; // 0x811c9dc5
const FNV_PRIME = 16777619; // 0x01000193

export type Hasher = {
  h: number;
};

export function createHasher(): Hasher {
  return { h: FNV_OFFSET_BASIS };
}

function mixByte(hasher: Hasher, byte: number): void {
  hasher.h = Math.imul(hasher.h ^ (byte & 0xff), FNV_PRIME);
}

export function mixInt(hasher: Hasher, x: number): void {
  // Mix in little-endian byte order.
  const u = x | 0;
  mixByte(hasher, u & 0xff);
  mixByte(hasher, (u >>> 8) & 0xff);
  mixByte(hasher, (u >>> 16) & 0xff);
  mixByte(hasher, (u >>> 24) & 0xff);
}

const _f64 = new Float64Array(1);
const _u8 = new Uint8Array(_f64.buffer);

export function mixFloat(hasher: Hasher, x: number): void {
  _f64[0] = x;
  for (let i = 0; i < 8; i++) {
    mixByte(hasher, _u8[i] ?? 0);
  }
}

export function mixString(hasher: Hasher, s: string): void {
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    mixByte(hasher, code & 0xff);
    mixByte(hasher, (code >>> 8) & 0xff);
  }
}

export function finalize(hasher: Hasher): number {
  return hasher.h >>> 0;
}
